import { useEffect, useState } from "react";
import { Fuel, BadgePercent, History, CloudDownload, RefreshCw, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { fmtDateTime } from "../../lib/format";

export default function FuelPriceSettings() {
  const [prices, setPrices] = useState([]);
  const [meta, setMeta] = useState({ updated_at: null, updated_by: null });
  const [apiInfo, setApiInfo] = useState({ enabled: false, last_sync: null, source_date: null, last_status: null });
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = () =>
    api.get("/fuel-prices").then((r) => {
      setPrices(r.data.items.map((p) => ({ ...p, budi_enabled: p.budi_price != null })));
      setMeta({ updated_at: r.data.updated_at, updated_by: r.data.updated_by });
      setApiInfo(r.data.api || {});
    });
  useEffect(() => { load(); }, []);

  const toggleAutoSync = async (enabled) => {
    setApiInfo((a) => ({ ...a, enabled }));
    try {
      await api.put("/fuel-prices/auto-sync", { enabled });
      toast.success(enabled ? "Penyelarasan automatik harian diaktifkan" : "Penyelarasan automatik dimatikan");
    } catch (e) {
      toast.error(apiError(e));
      setApiInfo((a) => ({ ...a, enabled: !enabled }));
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/fuel-prices/sync");
      if (data.changed.length > 0) {
        toast.success(`Harga dikemas kini (${data.changed.map((c) => `${c.fuel_type}: RM${c.new}`).join(", ")}) — tarikh kuat kuasa ${data.source_date}`);
      } else {
        toast.info(`Harga sudah terkini (tarikh kuat kuasa ${data.source_date})`);
      }
      load();
    } catch (e) {
      toast.error(apiError(e));
      load();
    } finally {
      setSyncing(false);
    }
  };

  const update = (i, patch) => setPrices((ps) => ps.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/fuel-prices", prices.map((p) => ({
        fuel_type: p.fuel_type,
        price_per_liter: Number(p.price_per_liter),
        budi_price: p.budi_enabled && p.budi_price ? Number(p.budi_price) : null,
      })));
      toast.success("Harga minyak dikemas kini");
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="fuel-price-settings">
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading text-base flex items-center gap-2">
            <CloudDownload className="h-4 w-4 text-[#10056E]" /> API Harga Minyak Rasmi (data.gov.my)
          </CardTitle>
          <CardDescription>
            Harga runcit rasmi Malaysia ditarik daripada <a href="https://data.gov.my/data-catalogue/fuelprice" target="_blank" rel="noreferrer" className="underline text-[#10056E]">api.data.gov.my</a> —
            tiada API key diperlukan. Semua jenis minyak di bawah diambil terus daripada API: RON95 (+ Harga BUDI95), RON97, Diesel Semenanjung (+ BUDI) dan Diesel Sabah &amp; Sarawak.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3 border rounded-lg p-3">
            <div>
              <p className="text-sm font-semibold">Selaraskan automatik setiap hari</p>
              <p className="text-xs text-muted-foreground">Sistem menyemak & mengemas kini harga sekali sehari secara automatik.</p>
            </div>
            <Switch checked={apiInfo.enabled} onCheckedChange={toggleAutoSync} data-testid="fuel-api-autosync-toggle" />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={syncNow} disabled={syncing} variant="outline" className="border-[#10056E] text-[#10056E] hover:bg-indigo-50" data-testid="fuel-api-sync-btn">
              {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              {syncing ? "Menyelaraskan..." : "Selaraskan Sekarang"}
            </Button>
            {apiInfo.last_sync && (
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground" data-testid="fuel-api-status">
                {apiInfo.last_status === "ok"
                  ? <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 border border-emerald-200"><CheckCircle2 className="h-3 w-3 mr-1" />Berjaya</Badge>
                  : <Badge variant="secondary" className="bg-red-50 text-red-700 border border-red-200"><XCircle className="h-3 w-3 mr-1" />Gagal</Badge>}
                <span>Penyelarasan terakhir: <b>{fmtDateTime(apiInfo.last_sync)}</b></span>
                {apiInfo.source_date && <span>· Tarikh kuat kuasa harga: <b>{apiInfo.source_date}</b></span>}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading text-base flex items-center gap-2"><Fuel className="h-4 w-4" /> Harga Minyak Semasa (RM/Liter)</CardTitle>
          <CardDescription>
            Harga ini digunakan untuk pengiraan automatik Liter/Jumlah pada borang Rekod Isi Minyak.
            Aktifkan Harga BUDI bagi jenis minyak yang layak subsidi.
          </CardDescription>
          {meta.updated_at && (
            <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-1" data-testid="fuel-price-last-updated">
              <History className="h-3.5 w-3.5 text-[#10056E]" />
              Kemas kini terakhir: <b>{fmtDateTime(meta.updated_at)}</b>{meta.updated_by ? <> oleh <b>{meta.updated_by}</b></> : null}
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {prices.map((p, i) => (
            <div key={p.fuel_type} className="border rounded-lg p-3 space-y-3" data-testid={`fuel-price-row-${p.fuel_type.replace(/[^a-zA-Z0-9]/g, "")}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm font-bold">{p.fuel_type}</p>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground">RM</Label>
                  <Input type="number" min="0.01" step="0.01" className="w-28 h-9" value={p.price_per_liter}
                    onChange={(e) => update(i, { price_per_liter: e.target.value })}
                    data-testid={`fuel-price-input-${p.fuel_type.replace(/[^a-zA-Z0-9]/g, "")}`} />
                  <span className="text-xs text-muted-foreground">/L</span>
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-3">
                <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <BadgePercent className="h-3.5 w-3.5 text-[#198754]" /> Harga BUDI (subsidi, pilihan)
                </p>
                <div className="flex items-center gap-2">
                  <Switch checked={p.budi_enabled} onCheckedChange={(v) => update(i, { budi_enabled: v })}
                    data-testid={`fuel-budi-toggle-${p.fuel_type.replace(/[^a-zA-Z0-9]/g, "")}`} />
                  {p.budi_enabled && (
                    <>
                      <Label className="text-xs text-muted-foreground">RM</Label>
                      <Input type="number" min="0.01" step="0.01" className="w-28 h-9" value={p.budi_price ?? ""}
                        onChange={(e) => update(i, { budi_price: e.target.value })}
                        data-testid={`fuel-budi-input-${p.fuel_type.replace(/[^a-zA-Z0-9]/g, "")}`} />
                      <span className="text-xs text-muted-foreground">/L</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
      <Button onClick={save} disabled={saving} className="bg-[#10056E] hover:bg-[#2a17a8]" data-testid="fuel-price-save-btn">
        {saving ? "Menyimpan..." : "Simpan Harga Minyak"}
      </Button>
    </div>
  );
}
